import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { AccountService } from '@infodocs/shared/services/account.service';
import { CompanyService } from '@infodocs/company/company.service';
import { CompanyTypes } from '@infodocs/company/variables/company-types';
import { CipcTypeId, CompanyType } from '@infodocs/company/variables/enums';
import { CompanyLimitNoticeComponent } from '@infodocs/portfolio/components/company-limit-notice/company-limit-notice.component';
import { AddCompanyModalComponent } from '@infodocs/portfolio/modals/add-company-modal/add-company-modal.component';
import { PortfolioCompanyDetailsFormComponent } from '@infodocs/portfolio/modals/add-company-modal/portfolio-company-details-form/portfolio-company-details-form.component';
import { CompanyInformationConfirmationComponent } from '@infodocs/portfolio/modals/add-company-modal/company-information-confirmation/company-information-confirmation.component';
import { DirectorsInformationComponent } from '@infodocs/portfolio/modals/add-company-modal/directors-information/directors-information.component';
import RegistersCompanyComponent from '@infodocs/portfolio/modals/add-company-modal/registers-company.component';
import {
	CompanyAddedJourneyLoggerService
} from '@infodocs/portfolio/services/company-added-journey-logger.service';
import { SubmitButtonComponent } from '@infodocs/shared/components/submit-button/submit-button.component';
import { DateSelectorComponent } from '@infodocs/shared/components/date-selector/date-selector.component';
import { IconComponent } from '@infodocs/shared/components/icon/icon.component';
import { StepComponent } from '@infodocs/shared/components/step/step.component';
import { StepperComponent } from '@infodocs/shared/components/stepper/stepper.component';
import {
	UploadDocumentsFormComponent
} from '@infodocs/shared/forms/upload-documents-form/upload-documents-form.component';
import {
	companyNameValidator,
	companyRegistrationNumberValidator,
} from '@infodocs/shared/forms/validators/custom-validators';
import { InfodocsDate } from '@infodocs/shared/interfaces/date.model';
import { DocumentService } from '@infodocs/shared/modules/documents/document.service';
import { ShouldShowErrorPipe } from '@infodocs/shared/pipes/should-show-error.pipe';
import {
	COMPANY_ALREADY_ADDED_MESSAGE,
	getFirstApiErrorMessage,
	HttpLikeError,
} from '@infodocs/shared/utils/company-search-errors';
import { infodocsDateToString } from '@infodocs/shared/utils/dates';
import { dateRequiredValidator } from '@infodocs/shared/validators/date';
import { DocumentGroupType, DocumentType } from '@infodocs/shared/variables/enums';
import { AlertModule } from 'ngx-bootstrap/alert';
import { catchError, filter, finalize, of, switchMap, takeUntil, tap } from 'rxjs';
import {
	Cor151AExtraction,
	mapEnterpriseType,
	mapExtractedAddress,
	mapFinancialYearEnd,
	mapIncorporationDate,
} from './cor151a-extraction';

@Component({
	selector: 'app-manual-add-company',
	imports: [
		StepperComponent,
		StepComponent,
		PortfolioCompanyDetailsFormComponent,
		SubmitButtonComponent,
		ReactiveFormsModule,
		ShouldShowErrorPipe,
		DateSelectorComponent,
		DirectorsInformationComponent,
		CompanyInformationConfirmationComponent,
		CompanyLimitNoticeComponent,
		UploadDocumentsFormComponent,
		AlertModule,
		IconComponent,
	],
	templateUrl: './manual-add-company.component.html',
	styleUrl: './manual-add-company.component.scss',
})
export class ManualAddCompanyComponent extends RegistersCompanyComponent implements OnInit, AfterViewInit {
	// Plain properties: set via ngx-bootstrap modalService.show initialState (Object.assign overwrites signals).
	public companyName?: string;
	public registrationNumber?: string;
	public onBack?: VoidFunction;
	protected readonly today = new Date();
	protected readonly faLockIcon = faLock;
	private static readonly uploadModalClass = 'modal-dialog';
	private static readonly formModalClass = 'modal-dialog modal-xl';
	protected readonly companyDetailsForm = new FormGroup({
		company_name: new FormControl(this.companyName ?? '', [Validators.required, companyNameValidator()]),
		company_type_id: new FormControl(Number(CompanyType.private_company), Validators.required),
		registration_number: new FormControl('', [Validators.required, companyRegistrationNumberValidator()]),
		tax_number: new FormControl('', Validators.pattern(/^\d{10}$/)),
		incorporation_date: new FormControl<InfodocsDate | null>(null, [Validators.required, dateRequiredValidator()]),
	});
	protected readonly companyForms = computed(() => [
		this.companyDetailsForm,
		...(this.companyDetailsFormComponent()?.companyForms() ?? []),
	]);
	protected readonly companyTypes = CompanyTypes;
	protected readonly isBusy = signal(false);
	protected readonly isExtracting = signal(false);
	protected readonly isDuplicateRegistration = signal(false);
	protected readonly isForeignCompany = signal(false);
	protected readonly cor151AFile = signal<File | undefined>(undefined);
	protected readonly uploadError = signal<string | undefined>(undefined);
	protected readonly documentPersistError = signal<string | undefined>(undefined);
	private readonly companyId = signal<number | undefined>(undefined);
	private readonly router = inject(Router);
	private readonly http = inject(HttpClient);
	private readonly accountService = inject(AccountService);
	private readonly documentService = inject(DocumentService);
	private readonly companyAddedJourneyLogger = inject(CompanyAddedJourneyLoggerService);

	protected areDirectorsFormsValid(): boolean {
		return this.directorStepControls()
			.filter(Boolean)
			.every(control => control!.valid);
	}

	protected directorStepControls() {
		return this.directorsInformationComponent()?.directorForms?.() ?? [];
	}

	protected directorsDetailsForReview() {
		return this.directorsInformationComponent()?.getDirectorsDetails?.() ?? [];
	}

	protected isCompanyFormsValid(): boolean {
		return this.companyForms()
			.filter(Boolean)
			.every(control => control!.valid);
	}

	public ngOnInit() {
		this.listenToRegistrationNumberChanges();
		this.listenToCompanyTypeChanges();
		this.prefillFromInitialState();
	}

	public ngAfterViewInit(): void {
		// Defer past the current CD cycle (AfterViewInit + checkNoChanges in tests).
		queueMicrotask(() => {
			if (this.isBlockedByCompanyLimit()) {
				this.showCompanyLimitStep();
			}
			void this.applyCompanyLimitGate();
		});
	}

	private prefillFromInitialState(): void {
		const companyName = this.companyName?.trim();
		const registrationNumber = this.registrationNumber?.trim();
		if (!companyName && !registrationNumber) {
			return;
		}

		this.companyDetailsForm.patchValue({
			...(companyName ? { company_name: companyName } : {}),
			...(registrationNumber ? { registration_number: registrationNumber } : {}),
		});
	}

	protected getCompanyDetails() {
		return {
			...this.companyDetailsFormComponent()?.getCompanyDetails(),
			...this.companyDetailsForm.value,
		};
	}

	protected override companyRegistrationDetails(): Record<any, any> {
		const directors = this.directorsInformationComponent()?.getDirectorsDetails?.() ?? [];
		const directorsForCoreg = directors.map(d => ({
			...d,
			date_of_birth: d.date_of_birth && !d.citizen
				? infodocsDateToString(d.date_of_birth, '/')
				: d.date_of_birth,
		}));
		return {
			company_id: null,
			cipc_type_id: CipcTypeId.new_company_registration,
			name_reservation: {},
			company: this.getCompanyDetails(),
			directors: directorsForCoreg,
			customer_code: '',
		};
	}

	protected onCorFilesChanged(files: File[]): void {
		if (!files.length) {
			this.cor151AFile.set(undefined);
			return;
		}

		const file = files[files.length - 1];
		if (file === this.cor151AFile()) {
			return;
		}

		if (!this.isPdfFile(file)) {
			this.uploadError.set('Please upload a PDF file.');
			this.cor151AFile.set(undefined);
			return;
		}

		if (this.isOversized(file)) {
			this.uploadError.set('Please upload a PDF of 8MB or less.');
			this.cor151AFile.set(undefined);
			return;
		}

		this.uploadError.set(undefined);
		this.cor151AFile.set(file);
	}

	protected onCorFilesRejected(rejected: Array<File & { reason?: string }>): void {
		const oversized = rejected.some(file => file.reason === 'size' || this.isOversized(file));
		this.uploadError.set(oversized
			? 'Please upload a PDF of 8MB or less.'
			: 'Please upload a PDF file.');
	}

	protected submitCorUpload(): void {
		const file = this.cor151AFile();
		if (!file || this.isExtracting()) {
			return;
		}

		this.extractFromCor151A(file);
	}

	protected showManualForm(): void {
		this.modalRef.setClass(ManualAddCompanyComponent.formModalClass);
		this.selectStepByTag('companyInformation');
	}

	protected showUploadView(): void {
		this.modalRef.setClass(ManualAddCompanyComponent.uploadModalClass);
		this.selectStepByTag('upload');
	}

	protected async submitCompanyRegistrationForm(): Promise<void> {
		if (this.isDuplicateRegistration()) {
			return;
		}

		if (await this.isBlockedByLiveCompanyLimit()) {
			this.showCompanyLimitStep();
			return;
		}

		this.isBusy.set(true);
		this.companyAddedJourneyLogger.resetSession();
		this.documentPersistError.set(undefined);

		this.http
			.post<{ company_id: number }>('/api/company/add/manual', this.companyRegistrationDetails())
			.pipe(
				tap(() => localStorage.removeItem(CompanyService.COMPANIES_LIST)),
				tap(res => this.afterCompanyCreated(res.company_id)),
				switchMap(res => this.persistCor151A(res.company_id)),
				finalize(() => this.isBusy.set(false)),
				takeUntil(this.destroy$)
			).subscribe({
				next: () => this.onCreateSucceeded(),
				error: err => this.onCreateFailed(err),
			});
	}

	protected goToDashboard() {
		this.modalRef?.hide();

		return this.router.navigateByUrl(`/company/profile/${this.companyId()}`, {
			state: { company_name: this.companyDetailsForm.get('company_name')?.value },
		});
	}

	protected goBack() {
		if (this.stepper()?.selectedTag() === 'companyInformation') {
			this.showUploadView();
			return;
		}

		if (this.stepper()?.selectedTag() === 'companyLimit') {
			this.showUploadView();
			return;
		}

		this.modalRef?.hide();

		if (this.onBack) {
			return this.onBack();
		}

		return this.modalService.show(AddCompanyModalComponent, {
			class: 'modal-dialog modal-xl',
		});
	}

	private extractFromCor151A(file: File): void {
		this.isExtracting.set(true);
		this.errorMessage.set(undefined);
		this.uploadError.set(undefined);
		const formData = new FormData();
		formData.append('cor15_1a', file);

		this.http.post<Cor151AExtraction>('/api/company/add/extract-from-cor15-1a', formData)
			.pipe(finalize(() => this.isExtracting.set(false)), takeUntil(this.destroy$))
			.subscribe({
				next: extraction => this.applyExtraction(extraction),
				error: err => this.uploadError.set(this.extractErrorMessage(err)),
			});
	}

	private applyExtraction(extraction: Cor151AExtraction): void {
		this.applyCompanyExtraction(extraction);
		this.directorsInformationComponent()?.setDirectors(extraction.directors ?? []);
		this.showManualForm();
		queueMicrotask(() => this.markExtractedFieldsTouched());
	}

	private applyCompanyExtraction(extraction: Cor151AExtraction): void {
		const typeId = mapEnterpriseType(extraction.enterprise_type);
		this.companyDetailsForm.patchValue({
			company_name: extraction.company_name ?? this.companyDetailsForm.value.company_name,
			registration_number: extraction.registration_number ?? '',
			...(typeId ? { company_type_id: typeId } : {}),
		}, { emitEvent: false });
		this.applyCompanyTypeRules(
			Number(typeId ?? this.companyDetailsForm.value.company_type_id),
			false
		);

		const incorporation = mapIncorporationDate(extraction.incorporation_date);
		if (incorporation) {
			this.companyDetailsForm.patchValue({ incorporation_date: incorporation });
		}

		this.companyDetailsFormComponent()?.patchExtractedDetails({
			financial_year_end: mapFinancialYearEnd(extraction.financial_year_end),
			physicalAddress: mapExtractedAddress(extraction.physical_address),
		});
	}

	private markExtractedFieldsTouched(): void {
		['company_name', 'company_type_id', 'registration_number', 'incorporation_date'].forEach(name => {
			const control = this.companyDetailsForm.get(name);
			if (control?.invalid) {
				control.markAsTouched();
			}
		});
		this.companyDetailsFormComponent()?.markMissingRequiredTouched();
	}

	private afterCompanyCreated(companyId: number): void {
		this.companyId.set(companyId);
		this.accountService.incrementAccountCompanyCount(true);
	}

	private persistCor151A(companyId: number) {
		const file = this.cor151AFile();
		if (!file) {
			return of(null);
		}

		return this.documentService.uploadFile(file, `${companyId}/CIPC/`, DocumentType.moi).pipe(
			switchMap(res => this.documentService.linkDocument(
				res.document_id,
				DocumentGroupType.company,
				companyId
			)),
			catchError(err => {
				this.documentPersistError.set(
					err.error?.message ?? 'The company was added, but the COR 15.1A could not be stored.'
				);
				return of(null);
			})
		);
	}

	private onCreateSucceeded(): void {
		this.showSuccessStep();
		this.companyAddedJourneyLogger.logAdded('manual_add', this.companyId() ?? null);
	}

	private onCreateFailed(err: HttpLikeError): void {
		this.companyAddedJourneyLogger.logFailed('manual_add', err, this.companyId() ?? null);
		if (err.status === 403) {
			this.showCompanyLimitStep();
			return;
		}

		const message = getFirstApiErrorMessage(err, 'Error submitting information');
		this.errorMessage.set(message);
		this.isDuplicateRegistration.set(message === COMPANY_ALREADY_ADDED_MESSAGE);
	}

	private extractErrorMessage(err: {
		error?: { message?: string; errors?: { cor15_1a?: string[] } };
	}): string {
		return err.error?.message
			?? err.error?.errors?.cor15_1a?.[0]
			?? 'Failed to extract company information from the COR 15.1A.';
	}

	private isPdfFile(file: File): boolean {
		return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
	}

	private isOversized(file: File): boolean {
		return file.size > this.documentService.maxFileSize;
	}

	private showCompanyLimitStep(): void {
		this.selectStepByTag('companyLimit');
	}

	private async applyCompanyLimitGate(): Promise<void> {
		if (await this.isBlockedByLiveCompanyLimit()) {
			this.showCompanyLimitStep();
			return;
		}

		if (this.stepper()?.selectedTag() === 'companyLimit') {
			this.showUploadView();
		}
	}

	private async isBlockedByLiveCompanyLimit(): Promise<boolean> {
		await this.accountService.refreshAccountProfile();
		return this.isBlockedByCompanyLimit();
	}

	private isBlockedByCompanyLimit(): boolean {
		return this.accountService.hasReachedCompanyLimit() || this.accountService.hasReachedFlexiPaygLimit();
	}

	private showSuccessStep(): void {
		this.selectStepByTag('success');
	}

	private selectStepByTag(tag: string): void {
		const stepIndex = this.stepper()?.steps().findIndex(step => step.tag() === tag) ?? -1;

		if (stepIndex >= 0) {
			// Bypass form validation so limit/success/upload can open from an incomplete step.
			this.stepper()?.selectedStep.set(stepIndex);
		}
	}

	private listenToRegistrationNumberChanges() {
		this.companyDetailsForm.controls.registration_number.valueChanges
			.pipe(filter(Boolean), takeUntil(this.destroy$))
			.subscribe(value => {
				this.clearDuplicateRegistrationState();
				if (value.length === 14 && value.includes('/')) {
					const year = value.split('/')[0];
					this.companyDetailsForm.get('incorporation_date')?.patchValue({ year, month: '', day: '' });
				}
			});
	}

	private clearDuplicateRegistrationState() {
		this.isDuplicateRegistration.set(false);
		if (this.errorMessage() === COMPANY_ALREADY_ADDED_MESSAGE) {
			this.errorMessage.set(undefined);
		}
	}

	private listenToCompanyTypeChanges() {
		this.companyDetailsForm.controls.company_type_id.valueChanges
			.pipe(filter(Boolean), takeUntil(this.destroy$))
			.subscribe(value => this.applyCompanyTypeRules(+value, true));
	}

	private applyCompanyTypeRules(typeId: number, resetAddress: boolean): void {
		if (typeId === CompanyType.default) {
			this.companyDetailsForm.get('company_name')?.setValidators(Validators.required);
			this.companyDetailsForm.get('registration_number')?.setValidators(Validators.required);
			this.isForeignCompany.set(true);
			if (resetAddress) {
				this.companyDetailsFormComponent()?.companyDetailsForm?.controls?.physicalAddress?.setValue(null);
			}
		} else {
			this.isForeignCompany.set(false);
			this.companyDetailsForm.get('company_name')?.setValidators([Validators.required, companyNameValidator()]);
			this.companyDetailsForm.get('registration_number')
				?.setValidators([Validators.required, companyRegistrationNumberValidator()]);
			if (resetAddress) {
				this.resetPhysicalAddress();
			}
		}

		this.companyDetailsForm.get('company_name')?.updateValueAndValidity();
		this.companyDetailsForm.get('registration_number')?.updateValueAndValidity();
	}

	private resetPhysicalAddress(): void {
		//@ts-ignore
		this.companyDetailsFormComponent()?.companyDetailsForm?.controls?.physicalAddress?.setValue({
			country: 'South Africa',
			postal_code: '',
			province: '',
			address_1: '',
			address_2: '',
		});
	}
}
